import React, { useMemo } from "react"
import { useDevice } from "hooks/useDevice"
import PropTypes from "prop-types"
import moment from "moment"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import styles from "../../../Calendar.module.css"
import DayCell from "./DayCell"

const DAY_KEY_FORMAT = "YYYY-MM-DD"
const dayKey = (m) => m.format(DAY_KEY_FORMAT)

const Month = ({ date, onChangeFilters, activeTab, crewIds, equipmentIds, categoryIds, roleIds }) => {
 const calendarDate = date
  const { calendarTimeOff, bookings } = useSelector((state) => state.calendar)
  const { t } = useTranslation()

  const monthStartDate = useMemo(() => moment(calendarDate).startOf("month"), [calendarDate])
  const monthEndDate = useMemo(() => moment(calendarDate).endOf("month"), [calendarDate])

  const gridStartDate = useMemo(() => moment(calendarDate).startOf("month").startOf("week"), [calendarDate])
  const gridEndDate = useMemo(() => moment(calendarDate).endOf("month").endOf("week"), [calendarDate])

  const calendarDaysCount = gridEndDate.diff(gridStartDate, "days") + 1

  const calendarDays = useMemo(
    () => new Array(calendarDaysCount).fill().map((_, index) => gridStartDate.clone().add(index, "days")),
    [gridStartDate, calendarDaysCount]
  )

  const { isMobile, isTablet } = useDevice()
  const isCompact = isMobile || isTablet

  const weekDayNames = useMemo(
    () => new Array(7).fill().map((_, index) => moment().day(index).format("ddd")),
    []
  )

  const timeOffByDay = useMemo(() => {
    return calendarDays.reduce((acc, day) => {
      const dayStr = dayKey(day)
      const items = (calendarTimeOff || [])
        .filter((toff) => moment(toff.day_date, ["M/D/YYYY", "YYYY-MM-DD"]).isSame(day, "day"))
        .map((toff) => {
          const start = moment(toff.start_time, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"])?.format("h:mm A")
          const end = moment(toff.end_time, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"])?.format("h:mm A")
          return `${start} - ${end}`
        })
      acc[dayStr] = items
      return acc
    }, {})
  }, [calendarDays, calendarTimeOff])

  const allDayTimeOffByDay = useMemo(() => {
    return calendarDays.reduce((acc, day) => {
      const isAllDay = calendarTimeOff?.some(
        (toff) => moment(toff.day_date, ["M/D/YYYY", "YYYY-MM-DD"]).isSame(day, "day") && Boolean(toff.all_day)
      )
      acc[dayKey(day)] = isAllDay || false
      return acc
    }, {})
  }, [calendarDays, calendarTimeOff])

  const hasBookingsByDay = useMemo(() => {
    return calendarDays.reduce((acc, day) => {
      acc[dayKey(day)] = (bookings || []).some((booking) => booking?.date === dayKey(day))
      return acc
    }, {})
  }, [calendarDays, bookings])

  const needsCrewSelection = activeTab === "crew" && !crewIds?.length && !roleIds?.length
  const needsEquipmentSelection = activeTab === "equipment" && !equipmentIds?.length && !categoryIds?.length
  const showSelectionPrompt = needsCrewSelection || needsEquipmentSelection
  const selectionMessage = needsCrewSelection
    ? t("calendar.select_crew_member", "Select Crew Member")
    : t("calendar.select_equipment", "Select Equipment")

  return (
    <div className={styles.monthWrapper}>
      {showSelectionPrompt && (
        <div className={styles.monthSelectionOverlay}>
          <span className={styles.monthSelectionMessage}>{selectionMessage}</span>
        </div>
      )}
      <div className={styles.monthScrollWrapper}>
        <table className={styles.monthTable}>
          <thead>
            <tr className={styles.monthHeader}>
              {weekDayNames.map((dayName) => (
                <td key={dayName} className={styles.weekDayName}>
                  {isCompact ? dayName[0] : dayName}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: Math.ceil(calendarDays.length / 7) }).map((_, rowIdx) => {
              const weekDays = calendarDays.slice(rowIdx * 7, rowIdx * 7 + 7)
              const weekKey = weekDays.map((day) => dayKey(day)).join("-")
              return (
                <tr key={weekKey}>
                  {weekDays?.map((day, colIdx) => {
                    const isPastDay = day.isBefore(moment().startOf("day"), "day")
                    const isCurrentMonth = day.month() === monthStartDate.month()
                    return (
                      <td
                        key={dayKey(day)}
                        className={[
                          styles.monthTableCell,
                          isCurrentMonth ? "" : styles.notCurrentMonthDay,
                          isPastDay ? styles.pastDay : "",
                          allDayTimeOffByDay[dayKey(day)] && !hasBookingsByDay[dayKey(day)] ? styles.monthTableCellAllDay : "",
                          "border-gray-lightest p-2 border-end border-bottom"
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <DayCell
                          day={day}
                          thisMonth={isCurrentMonth}
                          lastCol={colIdx === 6}
                          lastRow={rowIdx === Math.ceil(calendarDays.length / 7) - 1}
                          monthStartDate={dayKey(monthStartDate)}
                          monthEndDate={dayKey(monthEndDate)}
                          timeOffRanges={timeOffByDay[dayKey(day)] || []}
                          allDayTimeOff={allDayTimeOffByDay[dayKey(day)]}
                          onChangeFilters={onChangeFilters}
                          hideBookings={showSelectionPrompt}
                        />
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

Month.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  activeTab: PropTypes.string,
  crewIds: PropTypes.array,
  equipmentIds: PropTypes.array,
  categoryIds: PropTypes.array,
  roleIds: PropTypes.array,
}

export default Month
